import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

// Mapping des types de lots vers les images correspondantes
const PRIZE_IMAGES = {
  'passion': '/images/passion.jpg',
  'force': '/images/force.jpg',
  'complicite': '/images/complicité.jpg',
  'tendresse': '/images/tendresse.jpg',
  'amitie': '/images/amitié.jpg',
  'aventure': '/images/aventure.jpg',
  'try_again_1': null,
  'try_again_2': null
};

const Game = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [wheelConfig, setWheelConfig] = useState(null);
  const [participantName, setParticipantName] = useState('');
  const [remainingPlays, setRemainingPlays] = useState(null);
  const [totalPlays, setTotalPlays] = useState(null);
  const [currentPlayNumber, setCurrentPlayNumber] = useState(1);
  const [playHistory, setPlayHistory] = useState([]); // Stocker tous les résultats des tours
  const canvasRef = useRef(null);

  // Icônes par défaut pour chaque type de lot
  const getDefaultIcon = (prizeType) => {
    const icons = {
      'tendresse': '',
      'try_again_1': '',
      'amitie': '',
      'passion': '',
      'aventure': '',
      'try_again_2': '',
      'force': '',
      'complicite': ''
    };
    return icons[prizeType] || '';
  };

  const verifyToken = useCallback(async () => {
    try {
      const response = await api.get(`/game/verify/${token}`);
      if (!response.data.valid) {
        setError(response.data.error || 'Token invalide');
        setLoading(false);
      } else {
        setParticipantName(response.data.participant_name || 'Joueur');
        setRemainingPlays(response.data.remaining_plays ?? 1);
        setTotalPlays(response.data.total_plays ?? 1);
        // Charger l'historique des tours déjà joués depuis le backend
        if (response.data.plays_history && response.data.plays_history.length > 0) {
          setPlayHistory(response.data.plays_history);
        }
        setCurrentPlayNumber((response.data.total_plays ?? 1) - (response.data.remaining_plays ?? 1) + 1);
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la verification du token');
      setLoading(false);
    }
  }, [token]);

  const loadWheelConfig = useCallback(async () => {
    try {
      const response = await api.get('/game/wheel-config');
      setWheelConfig(response.data.segments);
    } catch (err) {
      console.error('Error loading wheel config:', err);
    }
  }, []);

  const drawWheel = useCallback((rotation = 0) => {
    const canvas = canvasRef.current;
    if (!canvas || !wheelConfig) return;

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 30;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Dessiner la bordure extérieure de la roue
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius + 10, 0, 2 * Math.PI);
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 8;
    ctx.stroke();

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);

    const segmentAngle = (2 * Math.PI) / wheelConfig.length;

    wheelConfig.forEach((segment, index) => {
      const startAngle = index * segmentAngle;
      const endAngle = (index + 1) * segmentAngle;

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = segment.color;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3;
      ctx.stroke();

      ctx.save();
      ctx.rotate(startAngle + segmentAngle / 2);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';

      // Utiliser l'icône de la base de données ou un emoji par défaut
      const iconDisplay = segment.icon || getDefaultIcon(segment.prize_type);

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 16px Arial';
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.shadowBlur = 4;
      ctx.fillText(segment.name, radius - 25, 0);
      ctx.font = '28px Arial';
      ctx.fillText(iconDisplay, radius - 12, 0);
      ctx.restore();
    });

    ctx.restore();

    // AIGUILLE en forme de triangle au centre qui pointe vers le segment gagnant
    // L'aiguille pointe vers le haut (12h) - indique le segment gagnant
    const pointerSize = 25; // Taille du triangle
    const pointerLength = radius - 50; // Longueur depuis le centre

    ctx.save();
    ctx.translate(centerX, centerY);

    // Triangle principal (aiguille) qui pointe vers le haut
    ctx.beginPath();
    ctx.moveTo(0, -pointerLength); // Pointe vers le haut (vers 12h)
    ctx.lineTo(-pointerSize, 0); // Base gauche au centre
    ctx.lineTo(pointerSize, 0); // Base droite au centre
    ctx.closePath();

    // Dégradé pour l'aiguille
    const gradient = ctx.createLinearGradient(0, -pointerLength, 0, 0);
    gradient.addColorStop(0, '#DC143C'); // Rouge vif à la pointe
    gradient.addColorStop(0.5, '#FF4500'); // Orange rouge au milieu
    gradient.addColorStop(1, '#FFD700'); // Or à la base
    ctx.fillStyle = gradient;
    ctx.fill();

    // Bordure noire pour le contraste
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Ajouter un effet de brillance sur l'aiguille
    ctx.beginPath();
    ctx.moveTo(0, -pointerLength);
    ctx.lineTo(-pointerSize / 3, 0);
    ctx.lineTo(0, -10);
    ctx.closePath();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.fill();

    // Pointe rouge très visible au sommet
    ctx.beginPath();
    ctx.arc(0, -pointerLength + 8, 6, 0, 2 * Math.PI);
    ctx.fillStyle = '#8B0000';
    ctx.fill();
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.restore();

    // Cercle central pour couvrir la base de l'aiguille et donner un effet de pivot
    ctx.beginPath();
    ctx.arc(centerX, centerY, pointerSize - 5, 0, 2 * Math.PI);
    ctx.fillStyle = '#1a1a2e';
    ctx.fill();
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Cercle intérieur avec logo
    ctx.beginPath();
    ctx.arc(centerX, centerY, pointerSize - 12, 0, 2 * Math.PI);
    ctx.fillStyle = '#FFD700';
    ctx.fill();

    // Éclair en or au centre
    ctx.fillStyle = '#1a1a2e';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('⚡', centerX, centerY);
  }, [wheelConfig]);

  useEffect(() => {
    verifyToken();
    loadWheelConfig();
  }, [verifyToken, loadWheelConfig]);

  useEffect(() => {
    if (wheelConfig) {
      drawWheel();
    }
  }, [wheelConfig, drawWheel]);

  const spinWheel = async () => {
    setSpinning(true);
    setResult(null);

    try {
      const response = await api.post('/game/spin', { token });
      const data = response.data;

      // Update remaining plays from response
      setRemainingPlays(data.remaining_plays ?? 0);
      setResult(data);

      // Store this play result in history with the play number
      setPlayHistory(prev => [...prev, {
        playNumber: currentPlayNumber,
        ...data
      }]);

      const targetAngle = data.spin_angle;
      const spins = 5;
      const totalRotation = spins * 360 + targetAngle;
      const duration = 5000;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentRotation = totalRotation * easeOut;

        drawWheel(currentRotation);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Animation complete
          setTimeout(() => {
            setSpinning(false);

            // If plays are exhausted, redirect to result page
            // L'historique est maintenant stocké dans la base de données par le backend
            if (data.plays_exhausted) {
              navigate(`/result/${token}`);
            } else {
              // More plays available - increment current play number
              setCurrentPlayNumber(prev => prev + 1);
            }
          }, 3000);
        }
      };

      animate();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du spin');
      setSpinning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="text-center text-white">
          <div className="spinner-border mb-3" role="status" style={{ color: '#FFD700' }}>
            <span className="visually-hidden">Chargement...</span>
          </div>
          <h3 style={{ color: '#FFD700' }}>Chargement du jeu...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="card text-center p-5 shadow-lg" style={{ maxWidth: '500px', borderRadius: '15px', background: 'rgba(26, 26, 46, 0.9)', border: '2px solid #FFD700' }}>
          <span className="fa-solid fa-circle-exclamation fa-4x mb-3" style={{ color: '#FFD700' }}></span>
          <h3 style={{ color: '#FFD700' }}>Erreur</h3>
          <p className="text-light">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
      <div className="container py-4 py-md-5">
        {/* REAKTOR Header */}
        <div className="text-center mb-4">
          <div className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-lg mb-3"
            style={{
              width: 'clamp(70px, 15vw, 100px)',
              height: 'clamp(70px, 15vw, 100px)',
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              boxShadow: '0 0 25px rgba(255, 215, 0, 0.4)'
            }}>
            <span className="fa-solid fa-bolt" style={{ fontSize: 'clamp(25px, 8vw, 50px)', color: '#1a1a2e' }}></span>
          </div>
          <h1 className="fw-bold mb-2" style={{ fontSize: 'clamp(1.8rem, 5vw, 3rem)', color: '#FFD700' }}>
            La Roue des Emotions
          </h1>
          <h2 className="text-white-75" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: '#FFA500' }}>Saint-Valentin REAKTOR 2026</h2>
          <div className="mt-3">
            <span className="badge px-4 py-2 me-2" style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#1a1a2e', fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}>
              <span className="fa-solid fa-user me-2"></span>
              {participantName}
            </span>
            {totalPlays !== null && totalPlays > 1 && (
              <span className="badge px-4 py-2" style={{ background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)', color: '#fff', fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}>
                <span className="fa-solid fa-gamepad me-2"></span>
                Tour {currentPlayNumber} / {totalPlays}
                {remainingPlays !== null && (
                  <span className="ms-2">({remainingPlays} restant{remainingPlays > 1 ? 's' : ''})</span>
                )}
              </span>
            )}
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-7">
            <div className="card border-0 shadow-lg" style={{
              borderRadius: '25px',
              background: 'rgba(26, 26, 46, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 215, 0, 0.3)',
              boxShadow: '0 8px 32px rgba(255, 215, 0, 0.15)'
            }}>
              <div className="card-body p-4 p-md-5">
                <div className="text-center mb-4">
                  <canvas
                    ref={canvasRef}
                    width="400"
                    height="400"
                    className="mx-auto"
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                </div>

                <div className="text-center">
                  {!spinning ? (
                    result ? (
                      // Result shown - check if more plays available
                      remainingPlays !== null && remainingPlays > 0 ? (
                        <button
                          onClick={() => {
                            setResult(null);
                            spinWheel();
                          }}
                          className="btn btn-lg fw-bold px-5 py-3 rounded-pill shadow"
                          style={{
                            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                            border: 'none',
                            color: '#fff',
                            fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                            boxShadow: '0 4px 15px rgba(82, 196, 26, 0.4)',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.05)';
                            e.target.style.boxShadow = '0 6px 20px rgba(82, 196, 26, 0.6)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 4px 15px rgba(82, 196, 26, 0.4)';
                          }}
                        >
                          <span className="fa-solid fa-redo me-2"></span>
                          REJOUER ({remainingPlays} tour{remainingPlays > 1 ? 's' : ''} restant{remainingPlays > 1 ? 's' : ''})
                        </button>
                      ) : (
                        // All plays exhausted - show "close" message
                        <div className="p-4 rounded-4" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '2px solid #FFD700' }}>
                          <p className="mb-0 fw-bold" style={{ color: '#FFD700', fontSize: 'clamp(1rem, 2.5vw, 1.2rem)' }}>
                            <span className="fa-solid fa-check-circle me-2"></span>
                            Tous vos tours sont terminés !
                          </p>
                        </div>
                      )
                    ) : (
                      // No result yet - show spin button
                      <button
                        onClick={spinWheel}
                        className="btn btn-lg fw-bold px-5 py-3 rounded-pill shadow"
                        style={{
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          border: 'none',
                          color: '#1a1a2e',
                          fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                          boxShadow: '0 4px 15px rgba(255, 215, 0, 0.4)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'scale(1.05)';
                          e.target.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.6)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                          e.target.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.4)';
                        }}
                      >
                        <span className="fa-solid fa-bolt me-2"></span>
                        TOURNER LA ROUE !
                      </button>
                    )
                  ) : (
                    <div className="text-center">
                      <div className="spinner-border" role="status" style={{ width: '3.5rem', height: '3.5rem', color: '#FFD700' }}>
                        <span className="visually-hidden">Tourne...</span>
                      </div>
                      <p className="mt-3 fw-bold" style={{ color: '#FFD700', fontSize: 'clamp(1rem, 2.5vw, 1.2rem)' }}>
                        <span className="fa-solid fa-spinner fa-spin me-2"></span>
                        La roue tourne...
                      </p>
                    </div>
                  )}
                </div>

                {result && !spinning && (
                  <div className="mt-4 p-4 rounded-4 text-center" style={{
                    background: result.prize_code
                      ? 'linear-gradient(135deg, rgba(82, 196, 26, 0.2) 0%, rgba(82, 196, 26, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 165, 0, 0.2) 0%, rgba(255, 165, 0, 0.1) 100%)',
                    border: `2px solid ${result.prize_code ? '#52c41a' : '#ffa940'}`
                  }}>
                    <h4 className="mb-3" style={{ color: result.prize_code ? '#52c41a' : '#ffa940', fontSize: 'clamp(1.2rem, 3vw, 1.5rem)' }}>
                      {result.prize_code ? (
                        <>
                          <span className="fa-solid fa-trophy me-2"></span>Félicitations !
                        </>
                      ) : (
                        <>
                          <span className="fa-solid fa-redo me-2"></span>Réessayez !
                        </>
                      )}
                    </h4>

                    {/* Afficher l'image du lot gagné */}
                    {result.prize_type && PRIZE_IMAGES[result.prize_type] && (
                      <div className="mb-3 d-flex justify-content-center">
                        <img
                          src={PRIZE_IMAGES[result.prize_type]}
                          alt={result.prize_label || 'Lot gagné'}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '250px',
                            width: 'auto',
                            height: 'auto',
                            borderRadius: '12px',
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                            border: '3px solid #FFD700',
                            display: 'block'
                          }}
                          onLoad={(e) => {
                            console.log('Image loaded:', PRIZE_IMAGES[result.prize_type]);
                          }}
                          onError={(e) => {
                            console.error('Image failed to load:', PRIZE_IMAGES[result.prize_type]);
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    <p className="mb-3" style={{ color: '#fff', fontSize: 'clamp(1rem, 2.5vw, 1.2rem)' }}>{result.message}</p>
                    {result.prize_code && (
                      <div className="p-3 rounded-3" style={{ background: 'rgba(255, 215, 0, 0.15)', border: '2px solid #FFD700' }}>
                        <small style={{ color: '#FFD700', display: 'block', marginBottom: '8px' }}>Votre code gagnant :</small>
                        <span className="fw-bold" style={{
                          color: '#FFD700',
                          fontSize: 'clamp(1.3rem, 3vw, 1.8rem)',
                          fontFamily: 'monospace',
                          letterSpacing: '3px'
                        }}>
                          {result.prize_code}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-4 mt-md-5" style={{ color: '#FFD700' }}>
          <p className="mb-2" style={{ fontSize: 'clamp(0.95rem, 2vw, 1.1rem)' }}>
            <span className="fa-solid fa-gift me-2"></span>
            Gagnez des cadeaux exclusifs Reaktor
          </p>
          <small style={{ color: 'rgba(255, 215, 0, 0.75)', fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)' }}>
            Achat de 2 bouteilles minimum requis • 1 participation par jour
          </small>
        </div>

        {/* Historique des tours joués */}
        {playHistory.length > 0 && (
          <div className="mt-4">
            <div className="card border-0 shadow-lg" style={{
              borderRadius: '15px',
              background: 'rgba(26, 26, 46, 0.95)',
              border: '1px solid rgba(255, 215, 0, 0.2)'
            }}>
              <div className="card-body p-3 p-md-4">
                <h5 className="mb-3 text-center" style={{ color: '#FFD700', fontSize: 'clamp(1rem, 2vw, 1.2rem)' }}>
                  <span className="fa-solid fa-history me-2"></span>
                  Vos résultats ({playHistory.length}/{totalPlays})
                </h5>
                <div className="row g-2 g-md-3">
                  {playHistory.map((play, idx) => (
                    <div className="col-6 col-md-4" key={idx}>
                      <div className="p-2 p-md-3 rounded-3 text-center h-100" style={{
                        background: play.prize_code
                          ? 'linear-gradient(135deg, rgba(82, 196, 26, 0.2) 0%, rgba(82, 196, 26, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(255, 165, 0, 0.2) 0%, rgba(255, 165, 0, 0.1) 100%)',
                        border: `1px solid ${play.prize_code ? '#52c41a' : '#ffa940'}`
                      }}>
                        <div className="mb-1" style={{
                          fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontWeight: 600
                        }}>
                          Tour {play.playNumber}
                        </div>
                        {play.prize_code ? (
                          <>
                            <div className="mb-1" style={{
                              fontSize: 'clamp(0.9rem, 1.8vw, 1.1rem)',
                              color: '#52c41a',
                              fontWeight: 700
                            }}>
                              {play.prize_label || 'Gagné !'}
                            </div>
                            {play.prize_code && (
                              <div style={{
                                fontSize: 'clamp(0.7rem, 1.2vw, 0.8rem)',
                                color: '#FFD700',
                                fontFamily: 'monospace'
                              }}>
                                {play.prize_code}
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{
                            fontSize: 'clamp(0.9rem, 1.8vw, 1.1rem)',
                            color: '#ffa940'
                          }}>
                            Réessayez
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Game;
